/**
 * CRM v2 — stores React Query do motor de cards novo.
 *
 * Convenções do projeto: queryKey prefixo 'v1', polling 30s nas listas
 * vivas, mutations invalidam as keys tocadas. Valores em CENTAVOS ponta a
 * ponta (formatação só na borda da UI — diferente do motor antigo).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, ApiError, hasToken } from '@/lib/api';
import type { DadosDoContratoDoGanho } from '@/lib/contratos/contrato-do-ganho';
import type {
  AtencaoPayload,
  Automacao,
  AutomacaoExecucao,
  CardHistoricoResponsavelEvento,
  CatalogoServico,
  ConfigAlertas,
  CronometroEstado,
  CrmAtividade,
  CrmCard,
  CrmComentario,
  CrmDocumento,
  CrmTag,
  DocumentoExigidoGanho,
  EntregaHistoricoEvento,
  EvolucaoPayload,
  Fase,
  IndicadoresPayload,
  IntakeResultado,
  LembreteTempo,
  ModeloAquisicao,
  Lead,
  LeadHistoricoEvento,
  LeadStatus,
  MetaProgresso,
  MotivoSimples,
  OrigemLead,
  TipoAtividade,
  TravaGanho,
  Vendedor,
} from './crm-types';

const K = {
  config: (lista: string) => ['v1', 'crm', 'config', lista] as const,
  fases: ['v1', 'crm', 'config', 'fases'] as const,
  leads: (filtros: Record<string, string | undefined>) => ['v1', 'crm', 'leads', filtros] as const,
  lead: (id: string) => ['v1', 'crm', 'leads', 'single', id] as const,
  leadAtividades: (id: string) => ['v1', 'crm', 'leads', id, 'atividades'] as const,
  leadHistorico: (id: string) => ['v1', 'crm', 'leads', id, 'historico'] as const,
  cards: (filtros: Record<string, string | undefined>) => ['v1', 'crm', 'cards', filtros] as const,
  card: (id: string) => ['v1', 'crm', 'cards', 'single', id] as const,
  cardSub: (id: string, sub: string) => ['v1', 'crm', 'cards', id, sub] as const,
  agenda: (mes: string) => ['v1', 'crm', 'agenda', mes] as const,
  metas: (mes: string) => ['v1', 'crm', 'metas', mes] as const,
  automacoes: ['v1', 'crm', 'automacoes'] as const,
  automacaoExec: (id: string) => ['v1', 'crm', 'automacoes', id, 'execucoes'] as const,
  indicadores: (filtros: Record<string, string | undefined>) => ['v1', 'crm', 'indicadores', filtros] as const,
  atencao: (filtros: Record<string, string | undefined>) => ['v1', 'crm', 'indicadores', 'atencao', filtros] as const,
  configAlertas: ['v1', 'crm', 'config', 'alertas'] as const,
  ownership: (busca: string) => ['v1', 'crm', 'config', 'ownership', busca] as const,
  visibilidade: ['v1', 'crm', 'config', 'visibilidade'] as const,
  camposObrigatorios: ['v1', 'crm', 'config', 'campos-obrigatorios'] as const,
};

function qs(params: Record<string, string | undefined>): string {
  const pairs = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (pairs.length === 0) return '';
  return '?' + pairs.map(([k, v]) => `${k}=${encodeURIComponent(v!)}`).join('&');
}

// ─── Config ────────────────────────────────────────────────────────────

/**
 * 🔴 02/10/2026 — `incluirInativos` NÃO é detalhe: era um BUG, medido.
 *
 * O apagar destas listas é SOFT (`ativo = 0`), e a tela de Configurações pedia
 * a lista **sem os inativos**. ⚠️ Resultado: item desativado **sumia de
 * Configurações e não havia como religá-lo pela tela** — nem limpá-lo.
 *
 * **Medido no beta em 02/10:** 6 itens invisíveis — 4 origens de lead (duas
 * delas lixo de um smoke antigo), 1 documento exigido do Ganho e 1 tag.
 * 🔴 Foi assim que a lista de documentos exigidos ficou vazia para o Matheus:
 * o único item existia e estava desligado.
 *
 * ✅ **A tela de config pede `true`; quem CONSOME a lista pede `false`** —
 * ninguém escolhe um status desativado num card novo.
 */
function useConfigLista<T>(lista: string, incluirInativos = false) {
  return useQuery({
    queryKey: [...K.config(lista), { incluirInativos }],
    queryFn: () => api.get<T[]>(`/api/v1/crm/config/${lista}${incluirInativos ? '?incluir_inativos=1' : ''}`),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

export function useCatalogoServicos(incluirInativos = false) {
  return useConfigLista<CatalogoServico>('catalogo-servicos', incluirInativos);
}
/**
 * CA-1: `incluirInativas` só na TELA DE CONFIG (lista as fases desativadas
 * atenuadas, com botão Reativar). Kanban e demais telas seguem sem o flag —
 * fase desativada não vira coluna.
 */
export function useFases(incluirInativas = false) {
  return useQuery({
    queryKey: [...K.fases, { incluirInativas }] as const,
    queryFn: () =>
      api.get<Fase[]>(`/api/v1/crm/config/fases${incluirInativas ? '?incluir_inativos=1' : ''}`),
    enabled: hasToken(),
    // D-4 (decisão do Matheus): as fases atualizam SOZINHAS, no mesmo ritmo
    // dos cards — admin renomeia/cria uma fase e o funil de todo mundo
    // acompanha sem F5. O KanbanBoard congela o quadro durante um drag em
    // curso, então a atualização nunca muda as colunas debaixo do dedo.
    staleTime: 25_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}
export function useLeadStatus(incluirInativos = false) {
  return useConfigLista<LeadStatus>('lead-status', incluirInativos);
}
export function useOrigensLead(incluirInativos = false) {
  return useConfigLista<OrigemLead>('origens-lead', incluirInativos);
}
export function useMotivosPerda(incluirInativos = false) {
  return useConfigLista<MotivoSimples>('motivos-perda', incluirInativos);
}
export function useMotivosDescarte(incluirInativos = false) {
  return useConfigLista<MotivoSimples>('motivos-descarte', incluirInativos);
}

// PF11 — destino do lead que volta do descarte por prazo do motivo. Mora na
// config da reciclagem (KV). Mesma queryKey da aba Reciclagem pra o cache ficar
// coerente entre as duas telas.
export type RetornoDescarteDestino = 'mesmo_vendedor' | 'pool';
const RECICLAGEM_KEY = ['v1', 'crm', 'config', 'reciclagem'] as const;

export function useReciclagemDestino() {
  return useQuery({
    queryKey: RECICLAGEM_KEY,
    queryFn: () =>
      api.get<{ config: { retornoDescarteDestino: RetornoDescarteDestino } }>(
        '/api/v1/crm/config/reciclagem',
      ),
    enabled: hasToken(),
    staleTime: 60_000,
    select: (d) => d.config.retornoDescarteDestino,
  });
}

export function useSalvarReciclagemDestino() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (retornoDescarteDestino: RetornoDescarteDestino) =>
      api.put<{ config: unknown }>('/api/v1/crm/config/reciclagem', { retornoDescarteDestino }),
    onSuccess: (data) => {
      qc.setQueryData(RECICLAGEM_KEY, data);
      void qc.invalidateQueries({ queryKey: RECICLAGEM_KEY });
    },
  });
}
export function useDocumentosExigidos(incluirInativos = false) {
  return useConfigLista<DocumentoExigidoGanho>('documentos-exigidos', incluirInativos);
}
export function useTiposAtividadeCrm(incluirInativos = false) {
  return useConfigLista<TipoAtividade>('tipos-atividade', incluirInativos);
}
/** CA-6: catálogo de tempos de lembrete (crm_lembrete_tempos, mig 413). */
export function useLembreteTempos(incluirInativos = false) {
  return useConfigLista<LembreteTempo>('lembrete-tempos', incluirInativos);
}
export function useCrmTags(incluirInativos = false) {
  return useConfigLista<CrmTag>('tags', incluirInativos);
}

export function useCamposObrigatorios() {
  return useQuery({
    queryKey: K.camposObrigatorios,
    queryFn: () =>
      api.get<{ camposValidos: string[]; regras: { faseId: string; campo: string }[] }>(
        '/api/v1/crm/config/campos-obrigatorios',
      ),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

/** O16: nível de visibilidade dos Indicadores pro colaborador (configurável). */
export type IndicadoresNivel = 'proprios' | 'ranking_nomeado' | 'ranking_anonimo' | 'equipe' | 'tudo';

export function useCrmVisibilidade() {
  return useQuery({
    queryKey: K.visibilidade,
    queryFn: () =>
      api.get<{
        perfis: { slug: string; nome: string; escopo: 'proprios' | 'todos'; indicadoresNivel: IndicadoresNivel }[];
      }>('/api/v1/crm/config/visibilidade'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

/**
 * 🔴 U2 (29/09/2026) — `vendedor` é o RECORTE: `''` não filtra, `'sem'` traz
 * quem não tem dono, um id traz aquele vendedor.
 *
 * ⚠️ Ele entra na `queryKey`: sem isso, trocar o recorte devolveria a lista
 * anterior do cache e a tela mostraria o filtro aceso com o conteúdo errado.
 */
/** Um contrato vigente do cliente, como a Carteira mostra (`server/lib/contratos/contratos-da-carteira.ts`). */
export interface ContratoDaCarteira {
  id: string;
  situacao: 'ativo' | 'em_implantacao' | 'aviso_previo';
  planos: string[];
  sistemas: string[];
}

export function useOwnership(busca: string, vendedor = '', soComContrato = false) {
  return useQuery({
    // 19/10/2026: a chave "Só com contrato" também entra na queryKey (mesmo motivo do recorte).
    queryKey: K.ownership(`${busca}|${vendedor}|${soComContrato ? 'contrato' : ''}`),
    queryFn: () =>
      // BUGS #92: passou a devolver `{ items, total }` — a tela mostrava 100
      // de 761 e não dizia que cortou.
      api.get<{
        items: {
          clienteId: string;
          razaoSocial: string;
          nomeFantasia: string | null;
          cnpj: string | null;
          cidade: string | null;
          uf: string | null;
          vendedorResponsavelId: string | null;
          vendedorNome: string | null;
          /** 🔴 19/10/2026: os contratos da ficha em situação Ativo, Em implantação ou Aviso prévio (vazio = sem linha de contrato). */
          contratos: ContratoDaCarteira[];
        }[];
        total: number;
        /**
         * 🔴 Quantos cada vendedor tem dentro da busca atual — a chave `'sem'`
         * é quem está sem dono. Vem do MESMO `WHERE` da lista (REGRA 7).
         */
        contagens: Record<string, number>;
      }>(`/api/v1/crm/config/ownership${qs({ busca, vendedor, soComContrato: soComContrato ? '1' : undefined })}`),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

/** Ações genéricas de config (criar/editar/excluir item de qualquer lista). */
export function useConfigActions(lista: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'config'] });
  };
  const criar = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post(`/api/v1/crm/config/${lista}`, body),
    onSuccess: invalidate,
  });
  const editar = useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      api.put(`/api/v1/crm/config/${lista}/${id}`, body),
    onSuccess: invalidate,
  });
  const excluir = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/crm/config/${lista}/${id}`),
    onSuccess: invalidate,
  });
  return { criar, editar, excluir };
}

export function useConfigGeralActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'config'] });
  };
  const salvarCamposObrigatorios = useMutation({
    mutationFn: (body: { faseId: string; campos: string[] }) =>
      api.put('/api/v1/crm/config/campos-obrigatorios', body),
    onSuccess: invalidate,
  });
  const salvarVisibilidade = useMutation({
    mutationFn: (body: { perfil: string; escopo?: 'proprios' | 'todos'; indicadoresNivel?: IndicadoresNivel }) =>
      api.put('/api/v1/crm/config/visibilidade', body),
    onSuccess: invalidate,
  });
  const salvarOwnership = useMutation({
    mutationFn: (body: { clienteId: string; vendedorResponsavelId: string | null }) =>
      api.put('/api/v1/crm/config/ownership', body),
    onSuccess: invalidate,
  });
  return { salvarCamposObrigatorios, salvarVisibilidade, salvarOwnership };
}

// ─── Leads ─────────────────────────────────────────────────────────────

export interface LeadsFiltros {
  busca?: string;
  status?: string;
  origem?: string;
  responsavel?: string;
  /**
   * ⚠️ `semAcao` SAIU daqui em 26/09/2026 (item 29), e o que ele filtrava virou
   * a linha de contagem no topo do funil.
   * 🔴 **E o filtro dos LEADS estava errado desde sempre:** ele lia
   * `leads.proxima_acao_data`, coluna que o próprio `sem-acao.ts` documenta
   * como **vazia em todos os leads** — na prática o botão devolvia o quadro
   * inteiro dizendo que todo mundo estava parado. A contagem nova lê
   * `proximaTarefaDoLead`, o mesmo motor do card.
   * ⚠️ **Não confundir com o CAMPO `card.semAcao`**, que continua vivo e é o
   * que pinta o selo no card. O que saiu é o PARÂMETRO de filtro.
   */
  /** PF7: '1' = inclui os leads convertidos (que geraram proposta). */
  incluirConvertidos?: string;
}

export function useLeads(filtros: LeadsFiltros = {}) {
  return useQuery({
    queryKey: K.leads(filtros as Record<string, string | undefined>),
    queryFn: () => api.get<Lead[]>(`/api/v1/crm/leads${qs(filtros as Record<string, string | undefined>)}`),
    enabled: hasToken(),
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

export function useLeadAtividades(leadId: string | null) {
  return useQuery({
    queryKey: K.leadAtividades(leadId ?? ''),
    queryFn: () => api.get<CrmAtividade[]>(`/api/v1/crm/leads/${leadId}/atividades`),
    enabled: hasToken() && Boolean(leadId),
  });
}

/**
 * R4: histórico de mudanças de responsável do lead. `enabled` só quando há
 * lead — o caller passa `null` fora da aba Histórico pra não buscar à toa.
 */
export function useLeadHistorico(leadId: string | null) {
  return useQuery({
    queryKey: K.leadHistorico(leadId ?? ''),
    queryFn: () =>
      api.get<{ historico: LeadHistoricoEvento[] }>(`/api/v1/crm/leads/${leadId}/historico`),
    enabled: hasToken() && Boolean(leadId),
    select: (d) => d.historico,
  });
}

export function useLeadActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'leads'] });
    void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'cards'] });
  };
  const intake = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<IntakeResultado>('/api/v1/crm/leads/intake', body),
    onSuccess: invalidate,
  });
  const atualizar = useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      api.put<Lead>(`/api/v1/crm/leads/${id}`, body),
    onSuccess: invalidate,
  });
  const mudarStatus = useMutation({
    mutationFn: (input: { id: string; statusId: string; motivoDescarteId?: string | null }) =>
      api.put<Lead>(`/api/v1/crm/leads/${input.id}/status`, {
        statusId: input.statusId,
        motivoDescarteId: input.motivoDescarteId ?? null,
      }),
    onSuccess: invalidate,
  });
  const gerarPropostas = useMutation({
    mutationFn: (input: { id: string; propostas: Record<string, unknown>[] }) =>
      api.post<{ cards: CrmCard[] }>(`/api/v1/crm/leads/${input.id}/gerar-propostas`, {
        propostas: input.propostas,
      }),
    onSuccess: invalidate,
  });
  const criarAtividade = useMutation({
    mutationFn: ({ leadId, ...body }: Record<string, unknown> & { leadId: string }) =>
      api.post<CrmAtividade>(`/api/v1/crm/leads/${leadId}/atividades`, body),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: K.leadAtividades(vars.leadId) });
    },
  });
  /**
   * ⚠️ Saiu em 26/09 (BLOCO L). O `excluir` daqui chamava o `DELETE
   * /crm/leads/:id` antigo — **não-admin, sem porta na tela e com ZERO
   * consumidores medidos**. A exclusão agora é a mesma dos dois funis e mora
   * em `useExcluirDoComercial`.
   */
  /** R2: pegar lead do pool de Disponíveis. 409 = cooldown/teto/indisponível. */
  const pegar = useMutation({
    mutationFn: (id: string) => api.post<{ lead: Lead }>(`/api/v1/crm/leads/${id}/pegar`, {}),
    onSuccess: invalidate,
  });
  /** C1: dono (ou admin) devolve o lead pro pool de Disponíveis. */
  const liberarLead = useMutation({
    mutationFn: (id: string) => api.post<{ lead: Lead }>(`/api/v1/crm/leads/${id}/liberar`, {}),
    onSuccess: invalidate,
  });
  /** C1 (admin): passa o lead pra outro vendedor. */
  const reatribuirLead = useMutation({
    mutationFn: (input: { id: string; paraId: string }) =>
      api.post<{ lead: Lead }>(`/api/v1/crm/leads/${input.id}/reatribuir`, { paraId: input.paraId }),
    onSuccess: invalidate,
  });
  return {
    intake,
    atualizar,
    mudarStatus,
    gerarPropostas,
    criarAtividade,
    pegar,
    liberarLead,
    reatribuirLead,
  };
}

/**
 * C1/C2: mensagem pt-BR de erro das ações do CRM. O servidor manda
 * `{ error, message }` com `message` pronta pra UI (ex.: 403 de permissão) —
 * mostramos como veio; qualquer outro erro cai no fallback do caller.
 */
export function mensagemErroCrm(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.response && typeof err.response === 'object') {
    const body = err.response as { message?: unknown };
    if (typeof body.message === 'string' && body.message.trim()) return body.message;
  }
  return fallback;
}

// ─── Cards ─────────────────────────────────────────────────────────────

export interface CardsFiltros {
  busca?: string;
  fase?: string;
  tipo?: string;
  servico?: string;
  responsavel?: string;
  incluir_inativos?: string;
  /** ⚠️ `semAcao` saiu em 26/09 (item 29) — ver a nota em `LeadsFiltros`. */
}

export function useCrmCards(filtros: CardsFiltros = {}) {
  return useQuery({
    queryKey: K.cards(filtros as Record<string, string | undefined>),
    queryFn: () => api.get<CrmCard[]>(`/api/v1/crm/cards${qs(filtros as Record<string, string | undefined>)}`),
    enabled: hasToken(),
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

export function useCrmCard(id: string | null) {
  return useQuery({
    queryKey: K.card(id ?? ''),
    queryFn: () => api.get<CrmCard & { travaGanho: TravaGanho }>(`/api/v1/crm/cards/${id}`),
    enabled: hasToken() && Boolean(id),
  });
}

export function useCardAtividades(cardId: string | null) {
  return useQuery({
    queryKey: K.cardSub(cardId ?? '', 'atividades'),
    queryFn: () => api.get<CrmAtividade[]>(`/api/v1/crm/cards/${cardId}/atividades`),
    enabled: hasToken() && Boolean(cardId),
  });
}

export function useCardComentarios(cardId: string | null) {
  return useQuery({
    queryKey: K.cardSub(cardId ?? '', 'comentarios'),
    queryFn: () => api.get<CrmComentario[]>(`/api/v1/crm/cards/${cardId}/comentarios`),
    enabled: hasToken() && Boolean(cardId),
  });
}

export function useCardDocumentos(cardId: string | null) {
  return useQuery({
    queryKey: K.cardSub(cardId ?? '', 'documentos'),
    queryFn: () => api.get<CrmDocumento[]>(`/api/v1/crm/cards/${cardId}/documentos`),
    enabled: hasToken() && Boolean(cardId),
  });
}

/**
 * C2: histórico de mudanças de responsável do CARD — padrão do
 * useLeadHistorico (busca sob demanda: caller passa null fora da seção).
 */
export function useCardHistoricoResponsavel(cardId: string | null) {
  return useQuery({
    queryKey: K.cardSub(cardId ?? '', 'historico-responsavel'),
    queryFn: () =>
      api.get<{ historico: CardHistoricoResponsavelEvento[] }>(
        `/api/v1/crm/cards/${cardId}/historico-responsavel`,
      ),
    enabled: hasToken() && Boolean(cardId),
    select: (d) => d.historico,
  });
}

// F4 (COMERCIAL-AJUSTES): useCardVersoes removido — versionamento = revisões do orçamento.

/**
 * INCIDENTE-AGENDA (2026-08-05): a agenda é PESSOAL — sem `atribuidoA` o
 * servidor entrega só o escopo do usuário logado. O composer (aviso de
 * conflito) passa o RESPONSÁVEL escolhido explicitamente; nesse caso o
 * servidor devolve a agenda dele SEM os eventos Google (que são só do dono).
 */
export function useCrmAgenda(mesAno: string, atribuidoA?: string) {
  return useQuery({
    queryKey: [...K.agenda(mesAno), atribuidoA ?? 'me'],
    queryFn: () =>
      api.get<{
        tarefas: { id: string; cardId: string | null; leadId: string | null; titulo: string; data: string; concluida: boolean; atribuidoA: string | null }[];
        /** F1: eventos do Google Agenda do usuário (dedupados das tarefas). */
        eventosGoogle: { id: string; titulo: string; inicio: string; fim: string | null }[];
      }>(
        `/api/v1/crm/cards/agenda/${mesAno}${atribuidoA ? `?atribuidoA=${encodeURIComponent(atribuidoA)}` : ''}`,
      ),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

export function useCardActions() {
  const qc = useQueryClient();
  const invalidate = (cardId?: string) => {
    void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'cards'] });
    if (cardId) void qc.invalidateQueries({ queryKey: K.card(cardId) });
  };
  const criar = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<CrmCard>('/api/v1/crm/cards', body),
    onSuccess: () => invalidate(),
  });
  const atualizar = useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      api.put<CrmCard>(`/api/v1/crm/cards/${id}`, body),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
  const mudarFase = useMutation({
    mutationFn: (input: {
      id: string;
      faseId: string;
      motivoPerdaId?: string | null;
      /** Mig 379: move + define a data de entrega ('YYYY-MM-DDTHH:MM') num passo. */
      dataEntregaOrcamento?: string | null;
      /** 23/10/2026: o contrato preenchido no formulário do Ganho (card com mensalidade). */
      contratoDoGanho?: DadosDoContratoDoGanho | null;
    }) =>
      api.put<CrmCard>(`/api/v1/crm/cards/${input.id}/fase`, {
        faseId: input.faseId,
        motivoPerdaId: input.motivoPerdaId ?? null,
        ...(input.dataEntregaOrcamento ? { dataEntregaOrcamento: input.dataEntregaOrcamento } : {}),
        ...(input.contratoDoGanho ? { contratoDoGanho: input.contratoDoGanho } : {}),
      }),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
  /** Mig 379 / PF0-a: alterar a data de entrega do orçamento (grava no histórico). */
  const alterarEntrega = useMutation({
    mutationFn: (input: { id: string; dataEntrega: string; motivo?: string }) =>
      api.put<CrmCard>(`/api/v1/crm/cards/${input.id}/data-entrega`, {
        dataEntrega: input.dataEntrega,
        ...(input.motivo ? { motivo: input.motivo } : {}),
      }),
    onSuccess: (_d, vars) => {
      invalidate(vars.id);
      void qc.invalidateQueries({ queryKey: K.cardSub(vars.id, 'entrega-historico') });
    },
  });
  // D-1 (FILA-05AGO): arquivar exige MOTIVO (backend 422 sem ele) e é só-admin.
  // Condição do Matheus: metas/indicadores/ranking atualizam NA HORA — por
  // isso a invalidação alcança também as queries de indicadores e metas.
  const arquivar = useMutation({
    mutationFn: (input: { id: string; ativo: boolean; motivo?: string }) =>
      api.patch<CrmCard>(`/api/v1/crm/cards/${input.id}/ativo`, {
        ativo: input.ativo,
        ...(input.motivo ? { motivo: input.motivo } : {}),
      }),
    onSuccess: (_d, vars) => {
      invalidate(vars.id);
      void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'indicadores'] });
      void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'metas'] });
    },
  });
  const salvarTags = useMutation({
    mutationFn: (input: { id: string; tagIds: string[] }) =>
      api.put<CrmCard>(`/api/v1/crm/cards/${input.id}/tags`, { tagIds: input.tagIds }),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
  const criarAtividade = useMutation({
    mutationFn: ({ cardId, ...body }: Record<string, unknown> & { cardId: string }) =>
      api.post<CrmAtividade>(`/api/v1/crm/cards/${cardId}/atividades`, body),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: K.cardSub(vars.cardId, 'atividades') });
      invalidate(vars.cardId);
    },
  });
  const criarComentario = useMutation({
    mutationFn: (input: { cardId: string; texto: string }) =>
      api.post<CrmComentario>(`/api/v1/crm/cards/${input.cardId}/comentarios`, { texto: input.texto }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: K.cardSub(vars.cardId, 'comentarios') });
    },
  });
  const reagirComentario = useMutation({
    mutationFn: (input: { cardId: string; comentarioId: string; emoji: string }) =>
      api.post<CrmComentario>(`/api/v1/crm/cards/comentarios/${input.comentarioId}/reacoes`, {
        emoji: input.emoji,
      }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: K.cardSub(vars.cardId, 'comentarios') });
    },
  });
  const marcarLeitura = useMutation({
    mutationFn: (input: { cardId: string; comentarioId: string }) =>
      api.post(`/api/v1/crm/cards/comentarios/${input.comentarioId}/leitura`, {}),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: K.cardSub(vars.cardId, 'comentarios') });
    },
  });
  const fixarComentario = useMutation({
    mutationFn: (input: { cardId: string; comentarioId: string; fixado: boolean }) =>
      api.put<CrmComentario>(`/api/v1/crm/cards/comentarios/${input.comentarioId}/fixar`, {
        fixado: input.fixado,
      }),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: K.cardSub(vars.cardId, 'comentarios') });
    },
  });
  const registrarDocumento = useMutation({
    mutationFn: ({ cardId, ...body }: Record<string, unknown> & { cardId: string }) =>
      api.post<CrmDocumento>(`/api/v1/crm/cards/${cardId}/documentos`, body),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: K.cardSub(vars.cardId, 'documentos') });
      invalidate(vars.cardId);
    },
  });
  /** C2 (admin): passa a proposta pra outro responsável. */
  const reatribuirCard = useMutation({
    mutationFn: (input: { id: string; paraId: string }) =>
      api.post<{ card: CrmCard }>(`/api/v1/crm/cards/${input.id}/reatribuir`, { paraId: input.paraId }),
    onSuccess: (_d, vars) => {
      invalidate(vars.id);
      void qc.invalidateQueries({ queryKey: K.cardSub(vars.id, 'historico-responsavel') });
    },
  });
  /** C2 (admin): deixa a proposta sem responsável. */
  const liberarCard = useMutation({
    mutationFn: (id: string) => api.post<{ card: CrmCard }>(`/api/v1/crm/cards/${id}/liberar`, {}),
    onSuccess: (_d, id) => {
      invalidate(id);
      void qc.invalidateQueries({ queryKey: K.cardSub(id, 'historico-responsavel') });
    },
  });
  /** PF12: define o modelo de aquisição (compra/locação/financiamento) + prazo. */
  const alterarModeloAquisicao = useMutation({
    mutationFn: (input: { id: string; modelo: ModeloAquisicao; prazo?: number | null }) =>
      api.put<CrmCard>(`/api/v1/crm/cards/${input.id}/modelo-aquisicao`, {
        modelo: input.modelo,
        prazo: input.modelo === 'compra' ? null : (input.prazo ?? null),
      }),
    onSuccess: (_d, vars) => invalidate(vars.id),
  });
  return {
    criar,
    atualizar,
    mudarFase,
    alterarEntrega,
    arquivar,
    salvarTags,
    criarAtividade,
    criarComentario,
    reagirComentario,
    marcarLeitura,
    fixarComentario,
    registrarDocumento,
    reatribuirCard,
    liberarCard,
    alterarModeloAquisicao,
  };
}

/**
 * CA-5: concluir/reabrir tarefa — a MESMA rota serve tarefa de card e de lead
 * (a query do servidor é por id). O caller passa cardId/leadId só pra
 * invalidar a timeline certa; a agenda invalida sempre (Home + Calendário).
 */
export function useTarefaActions() {
  const qc = useQueryClient();
  const invalidate = (vars: { cardId?: string | null; leadId?: string | null }) => {
    if (vars.cardId) void qc.invalidateQueries({ queryKey: K.cardSub(vars.cardId, 'atividades') });
    if (vars.leadId) void qc.invalidateQueries({ queryKey: K.leadAtividades(vars.leadId) });
    void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'agenda'] });
    // C-1: concluir/reabrir tarefa agendada muda o selo "Sem ação" do card.
    // Antes só a timeline e a agenda recarregavam — o funil ficava com o selo
    // velho até alguém trocar de tela.
    if (vars.cardId) {
      void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'cards'] });
      void qc.invalidateQueries({ queryKey: K.card(vars.cardId) });
    }
  };
  const concluir = useMutation({
    mutationFn: (input: { atvId: string; cardId?: string | null; leadId?: string | null }) =>
      api.put<CrmAtividade>(`/api/v1/crm/cards/atividades/${input.atvId}/concluir`, {}),
    onSuccess: (_d, vars) => invalidate(vars),
  });
  const reabrir = useMutation({
    mutationFn: (input: { atvId: string; cardId?: string | null; leadId?: string | null }) =>
      api.put<CrmAtividade>(`/api/v1/crm/cards/atividades/${input.atvId}/reabrir`, {}),
    onSuccess: (_d, vars) => invalidate(vars),
  });
  return { concluir, reabrir };
}

// ─── CA-10: cronômetro do orçamentista (botão de 3 estados) ────────────

/** Estado do botão único do orçamentista (GET orcamento-cronometro). */
export function useOrcamentoCronometro(cardId: string | null) {
  return useQuery({
    queryKey: K.cardSub(cardId ?? '', 'orcamento-cronometro'),
    queryFn: () => api.get<CronometroEstado>(`/api/v1/crm/cards/${cardId}/orcamento-cronometro`),
    enabled: hasToken() && Boolean(cardId),
    staleTime: 15_000,
  });
}

export function useCronometroActions() {
  const qc = useQueryClient();
  const invalidate = (cardId: string) => {
    void qc.invalidateQueries({ queryKey: K.cardSub(cardId, 'orcamento-cronometro') });
    void qc.invalidateQueries({ queryKey: K.cardSub(cardId, 'atividades') });
    void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'cards'] });
  };
  const iniciarProducao = useMutation({
    mutationFn: (cardId: string) =>
      api.post<CronometroEstado>(`/api/v1/crm/cards/${cardId}/orcamento-cronometro/iniciar-producao`, {}),
    onSuccess: (_d, cardId) => invalidate(cardId),
  });
  const iniciarRevisao = useMutation({
    mutationFn: (cardId: string) =>
      api.post<CronometroEstado>(`/api/v1/crm/cards/${cardId}/orcamento-cronometro/iniciar-revisao`, {}),
    onSuccess: (_d, cardId) => invalidate(cardId),
  });
  const concluirRevisao = useMutation({
    mutationFn: (cardId: string) =>
      api.post<CronometroEstado>(`/api/v1/crm/cards/${cardId}/orcamento-cronometro/concluir-revisao`, {}),
    onSuccess: (_d, cardId) => invalidate(cardId),
  });
  return { iniciarProducao, iniciarRevisao, concluirRevisao };
}

/**
 * CA-10: mensagem pt-BR pros erros 422/409 do cronômetro (codes do backend).
 * Qualquer code desconhecido cai no fallback do caller.
 */
export function mensagemErroCronometro(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.response && typeof err.response === 'object') {
    const code = (err.response as { error?: unknown }).error;
    const mapa: Record<string, string> = {
      'fase-nao-gatilho': 'A proposta não está na fase de orçamento — mova o card antes de iniciar.',
      'orcamento-ja-concluido': 'Este orçamento já foi concluído — o próximo passo é a revisão, quando pedirem.',
      'producao-ja-iniciada': 'A produção já estava iniciada — o tempo segue contando.',
      'revisao-nao-solicitada': 'Não há pedido de revisão aberto pra esta proposta.',
      'revisao-ja-iniciada': 'A revisão já estava iniciada — o tempo segue contando.',
      'revisao-nao-iniciada': 'Inicie a revisão antes de concluir.',
    };
    if (typeof code === 'string' && mapa[code]) return mapa[code];
  }
  return fallback;
}

/** PF12: prazos configurados por simulador (locação GRENKE / financiamento SICOOB). */
export function usePrazosAquisicao() {
  return useQuery({
    queryKey: ['v1', 'crm', 'cards', 'modelo-aquisicao', 'prazos'] as const,
    queryFn: () => api.get<{ locacao: number[]; financiamento: number[] }>('/api/v1/crm/cards/modelo-aquisicao/prazos'),
    enabled: hasToken(),
    staleTime: 5 * 60 * 1000,
  });
}

/** PF9: campos personalizados PREENCHIDOS de um lead (read-only). */
/**
 * 🔴 02/10/2026 — LIGAR UM DOCUMENTO É GUARDAR O LINK, ❌ não subir o arquivo.
 *
 * Decisão do Matheus (14/09): *"não quero anexo de documentos no v2, mas sim o
 * link de acesso do documento que estará no Google Drive."* O upload para o R2
 * saiu daqui e a rota de `presign` recusa — ⚠️ se ela ainda atendesse, o
 * arquivo subiria e ficaria **órfão**, porque o registro não aceita mais chave
 * de arquivo.
 */
export async function ligarDocumentoCrm(
  cardId: string,
  nome: string,
  driveUrl: string,
  documentoExigidoId?: string | null,
): Promise<CrmDocumento> {
  return api.post<CrmDocumento>(`/api/v1/crm/cards/${cardId}/documentos`, {
    nome,
    driveUrl,
    documentoExigidoId: documentoExigidoId ?? null,
  });
}

export async function urlDocumentoCrm(docId: string): Promise<string> {
  const r = await api.get<{ url: string }>(`/api/v1/crm/cards/documentos/${docId}/url`);
  return r.url;
}

// ─── Metas ─────────────────────────────────────────────────────────────

/**
 * 🔴 ITEM 0 (30/09/2026) — `colaborador` é o filtro "Individual" da tela.
 *
 * ⚠️ **Ele entra na `queryKey`**, e isso não é detalhe: sem o colaborador na
 * chave, trocar de pessoa devolveria a lista do CACHE da pessoa anterior — o
 * filtro pareceria aplicado e os números seriam de outra. 🔴 **É o mesmo
 * cuidado do U2**, e aqui o dano é maior porque meta é número de cobrança.
 *
 * ⚠️ O servidor ignora o parâmetro de quem não vê a equipe: **o filtro de tela
 * nunca fura o escopo.**
 */
export function useMetasProgresso(mesAno: string, colaborador?: string) {
  return useQuery({
    queryKey: K.metas(`${mesAno}|${colaborador ?? ''}`),
    queryFn: () =>
      api.get<{ mesAno: string; metas: MetaProgresso[] }>(
        `/api/v1/crm/metas?mes=${mesAno}${colaborador ? `&colaborador=${encodeURIComponent(colaborador)}` : ''}`,
      ),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

export function useMetaActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'metas'] });
    void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'indicadores'] });
  };
  const criar = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/api/v1/crm/metas', body),
    onSuccess: invalidate,
  });
  const editar = useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      api.put(`/api/v1/crm/metas/${id}`, body),
    onSuccess: invalidate,
  });
  const excluir = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/crm/metas/${id}`),
    onSuccess: invalidate,
  });
  return { criar, editar, excluir };
}

// ─── Automações ────────────────────────────────────────────────────────

export function useCrmAutomacoes() {
  return useQuery({
    queryKey: K.automacoes,
    queryFn: () => api.get<Automacao[]>('/api/v1/crm/automacoes'),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

export function useAutomacaoExecucoes(automacaoId: string | null) {
  return useQuery({
    queryKey: K.automacaoExec(automacaoId ?? ''),
    queryFn: () => api.get<AutomacaoExecucao[]>(`/api/v1/crm/automacoes/${automacaoId}/execucoes`),
    enabled: hasToken() && Boolean(automacaoId),
  });
}

export function useAutomacaoActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: K.automacoes });
  };
  const criar = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Automacao>('/api/v1/crm/automacoes', body),
    onSuccess: invalidate,
  });
  const editar = useMutation({
    mutationFn: ({ id, ...body }: Record<string, unknown> & { id: string }) =>
      api.put<Automacao>(`/api/v1/crm/automacoes/${id}`, body),
    onSuccess: invalidate,
  });
  const excluir = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/crm/automacoes/${id}`),
    onSuccess: invalidate,
  });
  return { criar, editar, excluir };
}

// ─── Indicadores ───────────────────────────────────────────────────────

export interface IndicadoresQuery {
  /** G4 (CRM-HARDENING): período por intervalo de meses (YYYY-MM). */
  de?: string;
  ate?: string;
  /** @deprecated compat — o backend aceita `mes` (de=ate=mes). Prefira de/ate. */
  mes?: string;
  colaborador?: string;
  servico?: string;
  tipo?: string;
  origem?: string;
}

/** Mig 379 / PF0-a: histórico de alterações da data de entrega do orçamento. */
export function useCardEntregaHistorico(cardId: string | null) {
  return useQuery({
    queryKey: K.cardSub(cardId ?? '', 'entrega-historico'),
    queryFn: () => api.get<EntregaHistoricoEvento[]>(`/api/v1/crm/cards/${cardId}/data-entrega/historico`),
    enabled: hasToken() && Boolean(cardId),
  });
}

/** Mig 379: prazo sugerido do orçamento (dias úteis + data-hora pré-preenchida). */
export function usePrazoOrcamento() {
  return useQuery({
    queryKey: ['v1', 'crm', 'config', 'prazo-orcamento'] as const,
    queryFn: () =>
      api.get<{ prazoDiasUteis: number; dataEntregaSugerida: string }>(
        '/api/v1/crm/config/prazo-orcamento',
      ),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

export function useCrmIndicadores(filtros: IndicadoresQuery = {}) {
  return useQuery({
    queryKey: K.indicadores(filtros as Record<string, string | undefined>),
    queryFn: () =>
      api.get<IndicadoresPayloadCompleto>(`/api/v1/crm/indicadores${qs(filtros as Record<string, string | undefined>)}`),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

/**
 * INDICADORES-REDESIGN: vendedores "de verdade" (flag is_vendedor) — alimenta o
 * seletor do modo Individual e as cores/legendas dos gráficos de evolução.
 */
export function useVendedores() {
  return useQuery({
    queryKey: ['v1', 'crm', 'vendedores'] as const,
    queryFn: () => api.get<Vendedor[]>('/api/v1/crm/vendedores'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

export interface EvolucaoQuery {
  /**
   * C-6: a série cobre o PERÍODO escolhido na tela (`de`..`ate`, YYYY-MM). A
   * janela fixa de 6/12 meses saiu — dois recortes na mesma página se
   * contradiziam.
   */
  de: string;
  ate: string;
  colaborador?: string;
  servico?: string;
  origem?: string;
}

/**
 * INDICADORES-REDESIGN: 4 séries mensais (aba Análise). C-6: a janela é o
 * PERÍODO filtrado (`de`..`ate`). Mesma visibilidade da rota de indicadores.
 */
export function useEvolucao(filtros: EvolucaoQuery) {
  const params: Record<string, string | undefined> = {
    de: filtros.de,
    ate: filtros.ate,
    colaborador: filtros.colaborador,
    servico: filtros.servico,
    origem: filtros.origem,
  };
  return useQuery({
    queryKey: ['v1', 'crm', 'evolucao', params] as const,
    queryFn: () => api.get<EvolucaoPayload>(`/api/v1/crm/indicadores/evolucao${qs(params)}`),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

// ── Quadros "Precisa de atenção" + config de alertas (G5c) ─────────────

export interface AtencaoQuery {
  de?: string;
  ate?: string;
  colaborador?: string;
}

/**
 * G5c: worklist de higiene do Resumo (propostas/leads que precisam de atenção).
 * FOTO DO AGORA — o backend ignora o período (de/ate viajam só p/ invalidar o
 * cache junto com a tela); a visibilidade (escopo/colaborador) é respeitada.
 */
export function useAtencao(filtros: AtencaoQuery = {}) {
  const params: Record<string, string | undefined> = {
    de: filtros.de,
    ate: filtros.ate,
    colaborador: filtros.colaborador,
  };
  return useQuery({
    queryKey: K.atencao(params),
    queryFn: () => api.get<AtencaoPayload>(`/api/v1/crm/indicadores/atencao${qs(params)}`),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

/** G5c: cortes numéricos dos quadros de atenção (dias parado / sem proposta). */
export function useConfigAlertas() {
  return useQuery({
    queryKey: K.configAlertas,
    queryFn: () => api.get<ConfigAlertas>('/api/v1/crm/config/alertas'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

/** G5c (admin): salva os cortes de alerta. Invalida config + atenção. */
export function useSalvarConfigAlertas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<ConfigAlertas>) =>
      api.put<ConfigAlertas>('/api/v1/crm/config/alertas', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: K.configAlertas });
      void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'indicadores'] });
    },
  });
}

// ─── CL5: carteira automática (flag) ───────────────────────────────────
export function useCarteiraAutomatica() {
  return useQuery({
    queryKey: ['v1', 'crm', 'config', 'carteira-automatica'] as const,
    queryFn: () => api.get<{ ativa: boolean }>('/api/v1/crm/config/carteira-automatica'),
    enabled: hasToken(),
    staleTime: 60_000,
  });
}

export function useSalvarCarteiraAutomatica() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ativa: boolean) =>
      api.put<{ ativa: boolean }>('/api/v1/crm/config/carteira-automatica', { ativa }),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['v1', 'crm', 'config', 'carteira-automatica'] }),
  });
}

// ─── CL5: a completude configurável do Ganho morreu em 14/09/2026 (mig 606) ──

// ─── CA-7/CA-9/CA-10 (COMERCIAL-AJUSTES-2, fase 2) — payload de indicadores ─
// Campos NOVOS de GET /api/v1/crm/indicadores, espelhados 1:1 do servidor
// (server/lib/crm/crm-orcamentista.ts e server/lib/crm/crm-indicadores.ts).
// Ficam AQUI — e não no crm-types.ts — pra esta rodada não conflitar com
// edições paralelas; tudo opcional pra tolerar payload antigo em cache.

/** CA-7: tarefa agendada vencida e não concluída (drill-down por vendedor). */
export interface AtrasoTarefaItem {
  atividadeId: string;
  cardId: string | null;
  leadId: string | null;
  descricao: string;
  dataAgendada: string;
}

/** CA-7: proposta com a entrega do orçamento vencida. */
export interface AtrasoPropostaItem {
  cardId: string;
  numero: number;
  cliente: string;
  dataEntrega: string;
  diasAtraso: number;
}

/**
 * CA-7: atrasos de UM vendedor. `itens` tem teto de 50 (os mais antigos) —
 * `total` pode ser maior. Visibilidade já resolvida no servidor (admin vê
 * todos; vendedor só os seus).
 */
export interface AtrasosVendedor {
  vendedorId: string | null;
  nome: string | null;
  tarefasAtrasadas: { total: number; itens: AtrasoTarefaItem[] };
  propostasAtrasadas: { total: number; itens: AtrasoPropostaItem[] };
}

/**
 * CA-9 (mig 412): ciclo criação da proposta → desfecho (Ganho OU Perdido), em
 * dias corridos. SÓ cards com evento estruturado de fase (pós-deploy; sem
 * backfill do histórico em texto) — `amostra` = nº de cards medidos.
 */
export interface CicloPropostaDesfecho {
  mediaDias: number | null;
  amostra: number;
  porVendedor: { userId: string; nome: string; mediaDias: number; amostra: number }[];
}

/** CA-10: linha da quebra por orçamentista dos cronômetros. */
export interface OrcamentistaCiclosLinha {
  id: string | null;
  nome: string;
  ciclosProducao: number;
  producaoHoras: number | null;
  revisoes: number;
  filaHoras: number | null;
  revisaoHoras: number | null;
}

/**
 * CA-10 (mig 412): cronômetros do orçamento em HORAS CORRIDAS (1 casa) —
 * produção ("Fazer orçamento" → conferência), fila (solicitação → "Iniciar
 * revisão"; gargalo do admin) e revisão (início → conclusão/publicação).
 */
export interface OrcamentistaCiclos {
  tempoMedioProducaoHoras: number | null;
  tempoMedioFilaHoras: number | null;
  tempoMedioRevisaoHoras: number | null;
  totalCiclosProducao: number;
  totalRevisoes: number;
  porOrcamentista: OrcamentistaCiclosLinha[];
}

/** Payload dos indicadores COM os campos novos da rodada COMERCIAL-AJUSTES-2. */
export type IndicadoresPayloadCompleto = IndicadoresPayload & {
  /** CA-9: nome novo do antigo `cicloMedioDias` (alias deprecado ainda vem). */
  atividade: IndicadoresPayload['atividade'] & { cicloAteGanhoDias?: number | null };
  orcamentista: IndicadoresPayload['orcamentista'] & { ciclos?: OrcamentistaCiclos };
  atrasosPorVendedor?: AtrasosVendedor[];
  cicloPropostaDesfecho?: CicloPropostaDesfecho;
};

// ─── Lookup de leads (C6-A #1, 08/08/2026) ─────────────────────────────

/**
 * O mínimo para um seletor apontar para um lead — espelha o
 * `useClientesLookup`, inclusive na disciplina: projeção enxuta, e o campo
 * `convertido` só para a tela poder dizer que aquele lead já virou proposta.
 */
export interface LeadLookupItem {
  id: string;
  nome: string;
  convertido: boolean;
}

export function useLeadsLookup(opts: { search?: string; limit?: number } = {}) {
  const params = new URLSearchParams();
  if (opts.search) params.set('q', opts.search);
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return useQuery({
    queryKey: ['v1', 'crm', 'leads', 'lookup', opts.search ?? '', opts.limit ?? 0] as const,
    queryFn: () =>
      api.get<{ items: LeadLookupItem[] }>(`/api/v1/crm/leads/lookup${qs ? `?${qs}` : ''}`),
    enabled: hasToken(),
    staleTime: 30_000,
  });
}

/** Resolve o lead já escolhido, para o combobox mostrar o nome dele. */
export function useLeadLookupItem(id: string | null | undefined) {
  return useQuery({
    queryKey: ['v1', 'crm', 'leads', 'lookup', 'id', id ?? ''] as const,
    queryFn: () =>
      api.get<{ items: LeadLookupItem[] }>(
        `/api/v1/crm/leads/lookup?id=${encodeURIComponent(id!)}`,
      ),
    enabled: hasToken() && !!id,
    staleTime: 30_000,
  });
}
